import { Link } from "@tanstack/react-router";
import { usePageSeo } from "@/hooks/use-page-seo";
import { pageSeo } from "@/lib/seo";
import { ArrowRight, Handshake, HeartHandshake, MapPin, Wrench } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fadeUp, stagger } from "@/lib/animations";

interface ValueProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const Value = ({ icon: Icon, title, description }: ValueProps) => (
  <div className="rounded-xl border border-neutral-100 bg-white p-6 shadow-sm">
    <Icon className="mb-4 h-6 w-6 text-neutral-700" aria-hidden="true" />
    <h3 className="font-semibold text-neutral-900">{title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-neutral-600">{description}</p>
  </div>
);

const AboutPage = () => {
  usePageSeo(pageSeo.about);
  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="relative w-full overflow-hidden py-20">
        <img
          src="/allimages/dev team.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover scale-105"
          style={{ filter: "blur(4px)" }}
        />
        <div className="absolute inset-0 bg-white/80" />
        <motion.div
          className="relative z-10 mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.h1
            variants={fadeUp}
            className="mb-6 text-4xl font-bold tracking-tight text-neutral-900 md:text-5xl"
          >
            About Arsoc
          </motion.h1>
          <motion.p variants={fadeUp} className="text-lg leading-relaxed text-neutral-600">
            We are a technology company based in Zimbabwe, building software, strengthening
            information security, and delivering the networking and CCTV hardware that help
            businesses run better and serve their customers more effectively.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Story ── */}
      <section className="w-full py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid gap-12 lg:grid-cols-2 lg:gap-20"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            <motion.div variants={fadeUp}>
              <h2 className="text-2xl font-bold text-neutral-900">Who we are</h2>
              <div className="mt-4 space-y-4 leading-relaxed text-neutral-600">
                <p>
                  Arsoc is a technology company — and a member of the Rothkos Group — that
                  builds custom business platforms for organisations in Zimbabwe and the
                  broader African market. We work with businesses that need real operational
                  software — systems that fit how they actually work, not how a generic
                  product assumes they should.
                </p>
                <p>
                  Our work spans business management platforms, property management,
                  WhatsApp-integrated customer communication tools, information security
                  services, and hardware infrastructure — including networking and CCTV
                  installations. Everything we build and deploy is designed to be practical
                  in the real operational environment our clients work in — one that often
                  looks very different from the assumptions baked into Western off-the-shelf
                  software.
                </p>
                <p>
                  We are selective about the work we take on. We would rather do fewer
                  projects properly than spread ourselves thin. That means each client gets
                  focused attention from a team that is genuinely invested in making their
                  system work.
                </p>
              </div>
            </motion.div>
            <motion.div variants={fadeUp}>
              <h2 className="text-2xl font-bold text-neutral-900">Our approach</h2>
              <div className="mt-4 space-y-4 leading-relaxed text-neutral-600">
                <p>
                  We start every engagement with discovery — not proposals. Before we write
                  a line of code, we take time to understand the business: how decisions are
                  made, where the friction is, what data matters, and what success actually
                  looks like for the people using the system day to day.
                </p>
                <p>
                  We build iteratively and stay close to the client throughout. We don't
                  disappear for months and return with something that missed the point.
                  We show work, get feedback, and adjust — so that what we deliver actually
                  matches what was needed.
                </p>
                <p>
                  And we stay after launch. Software doesn't end at go-live. We provide
                  ongoing maintenance, support, and improvements — treating each client's
                  system as a living product rather than a delivered artefact.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="w-full bg-stone-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-12 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-neutral-900">What we stand for</h2>
            <p className="mt-4 text-neutral-600">
              Four principles that shape how we work with every client.
            </p>
          </motion.div>
          <motion.div
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {[
              { icon: HeartHandshake, title: "Listen first", description: "We don't lead with solutions. We lead with questions. Understanding your business deeply is how we build software that actually fits." },
              { icon: Handshake, title: "Genuine partnerships", description: "We treat every engagement as a long-term relationship. Your success is our measure of success — not the number of features shipped." },
              { icon: Wrench, title: "Stay involved", description: "We don't hand over code and walk away. We stay connected, keep systems healthy, and keep improving what we've built." },
              { icon: MapPin, title: "Local context", description: "We build with the realities of the Zimbabwean and African business environment in mind. Our software doesn't assume a Western context." },
            ].map(({ icon, title, description }) => (
              <motion.div key={title} variants={fadeUp}>
                <Value icon={icon} title={title} description={description} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Team ── */}
      <section className="w-full py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="mb-8 text-center text-2xl font-bold text-neutral-900">The team</h2>
            <div className="overflow-hidden rounded-2xl">
              <img
                src="/allimages/devteam 2.jpg"
                alt="The Arsoc development team"
                className="h-80 w-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="w-full border-t border-neutral-100 bg-stone-50 py-16">
        <motion.div
          className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 variants={fadeUp} className="text-2xl font-bold text-neutral-900">
            Want to work with us?
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-3 text-neutral-600">
            Tell us about your project and we'll have an honest conversation about whether
            we're the right team for it.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-6">
            <Button asChild size="lg">
              <Link to="/contact">
                Get in touch <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutPage;
